import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Plus, MapPin, Phone, CaretRight, FolderOpen, CircleNotch } from "@phosphor-icons/react";
import { api, STATUS_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const GROUP_FILTERS = {
  "created,forwarded_to_manager,with_manager,forwarded_to_field,with_field_officer": "Handover pending (all)",
  "submitted_to_discom,resolved": "Waiting for indent (all)",
};

const Row = ({ c }) => (
  <Link to={`/customers/${c.id}`} data-testid={`customer-link-${c.id}`}
    className="group grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_2fr_1fr_1fr_100px_auto_auto] items-center gap-3 md:gap-4 px-4 py-3.5 hover:bg-slate-50 transition-colors">
    <span className="w-10 h-10 rounded-full bg-brand-50 text-brand-700 font-heading font-bold flex items-center justify-center">{(c.name || "?").slice(0, 1).toUpperCase()}</span>
    <div className="min-w-0">
      <div className="font-semibold text-[14.5px] text-slate-900 truncate">{c.name}</div>
      <div className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin size={11} className="shrink-0" /> {c.address || "—"}</div>
      <div className="md:hidden mt-1.5"><StatusBadge status={c.status} /></div>
    </div>
    <div className="hidden md:block text-xs font-mono text-slate-600 truncate">{c.consumer_no || "—"}</div>
    <div className="hidden md:block text-xs font-mono text-slate-500 truncate">{c.pm_registration_no || "—"}</div>
    <div className="hidden md:block text-xs capitalize text-slate-600">{c.payment_mode || "—"}</div>
    <div className="hidden md:block"><StatusBadge status={c.status} /></div>
    <CaretRight size={16} className="text-slate-300 group-hover:text-brand-600 transition-colors" />
  </Link>
);

export default function Customers() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [loading, setLoading] = useState(true);
  const canCreate = (user.permissions || []).includes("create_customer");

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/customers", { params }).then((r) => { setCustomers(r.data); setLoading(false); });
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-7xl mx-auto space-y-4 stagger-in" data-testid="customers-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900">Customer files</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? "Loading…" : `${customers.length} ${customers.length === 1 ? "file" : "files"}`}</p>
        </div>
        {canCreate && (
          <Link to="/customers/new" data-testid="new-customer-button" aria-label="New customer"
            className="tap h-11 w-11 sm:w-auto sm:px-5 rounded-full sm:rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold flex items-center justify-center gap-1.5 shadow-lg shadow-brand-600/25 active:scale-95 transition-[background-color,transform]">
            <Plus size={18} weight="bold" /><span className="hidden sm:inline">New customer</span>
          </Link>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, consumer or registration no."
            className="w-full h-11 pl-10 pr-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="status-filter" className="sm:w-[240px] h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {user.role !== "technician" && Object.entries(GROUP_FILTERS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            {Object.entries(STATUS_META)
              .filter(([k]) => user.role !== "technician" || ["meter_configured", "completed"].includes(k))
              .map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden">
        <div className="hidden md:grid grid-cols-[auto_2fr_1fr_1fr_100px_auto_auto] gap-4 px-4 py-2.5 border-b border-slate-100 bg-slate-50/70 text-[10.5px] uppercase tracking-[0.12em] font-bold text-slate-500">
          <span className="w-10" /><span>Customer</span><span>Consumer no.</span><span>Registration no.</span><span>Payment</span><span>Status</span><span className="w-4" />
        </div>
        {loading ? (
          <div className="p-12 flex justify-center"><CircleNotch size={26} className="animate-spin text-slate-400" /></div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="empty-customers"><FolderOpen size={34} weight="duotone" className="mx-auto mb-2 text-slate-300" />No customer files match your search.</div>
        ) : (
          <div className="divide-y divide-slate-100">{customers.map((c) => <Row key={c.id} c={c} />)}</div>
        )}
      </div>
    </div>
  );
}
